import React, { useState } from 'react';
import {
  Box, Button, Card, Stack, Typography,
} from '@mui/material';
import {
  CheckCircleOutline as DoneIcon,
  ErrorOutline as WarnIcon,
  InfoOutlined as InfoIcon,
  Refresh as RecheckIcon,
  UploadFileOutlined as UploadIcon,
} from '@mui/icons-material';
import { WeekReview, WeekReviewItem } from '../../api/services/payroll.service';

// What the week needed beyond the statement, in sentences: who was in
// training and is paid training days, whose stops went to the driver who
// trained them, which routes had two people on them. Each change the
// review made can be undone here (and made again); a warning says what to
// do. Written by the server (services/payroll/weekReview.js) each time the
// statement, the worksheet, a training period or someone's pay changes.

const TONE = {
  warn: { icon: WarnIcon, color: 'warning.main' },
  done: { icon: DoneIcon, color: 'success.main' },
  info: { icon: InfoIcon, color: 'text.secondary' },
} as const;

const FOLDED = 4;

const Item: React.FC<{
  item: WeekReviewItem;
  locked: boolean;
  busy: boolean;
  onUndo: (key: string, undo: boolean) => void;
}> = ({
  item, locked, busy, onUndo,
}) => {
  const { icon: Icon, color } = TONE[item.tone];
  return (
    <Stack direction="row" gap={1.25} alignItems="flex-start" sx={{ py: 0.75 }}>
      <Icon sx={{ fontSize: 18, mt: 0.25, color, flexShrink: 0 }} />
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, color: item.undone ? 'text.secondary' : 'text.primary' }}>{item.text}</Typography>
      {item.undoable && !locked && (
        <Button size="small" disabled={busy} onClick={() => onUndo(item.key, !item.undone)} sx={{ py: 0, minWidth: 0, flexShrink: 0 }}>
          {item.undone ? 'Do it' : 'Undo'}
        </Button>
      )}
    </Stack>
  );
};

const WeekReviewCard: React.FC<{
  review: WeekReview | undefined;
  locked: boolean;
  busy: boolean;
  onRecheck: () => void;
  onUndo: (key: string, undo: boolean) => void;
  onAddWorksheet: () => void;
}> = ({
  review, locked, busy, onRecheck, onUndo, onAddWorksheet,
}) => {
  const [open, setOpen] = useState(false);
  if (!review?.at && locked) return null;
  const items = review?.items || [];
  const changes = items.filter((i) => i.tone === 'done' && !i.undone).length;
  const warnings = items.filter((i) => i.tone === 'warn').length;
  const noSheet = items.some((i) => i.kind === 'no_worksheet');
  const shown = open ? items : items.slice(0, FOLDED);

  return (
    <Card sx={{ mb: 2, px: 2, py: 1.5 }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle2">What this week needed</Typography>
          <Typography variant="caption" color="text.secondary">
            {!review?.at ? 'Not checked yet.'
              : !items.length ? 'Nothing beyond the statement: no training, no shared routes.'
                : [
                  changes ? `${changes} ${changes === 1 ? 'change' : 'changes'} made for you` : null,
                  warnings ? `${warnings} to look at` : null,
                  'checked against the statement, the Weekly Service Worksheet and training',
                ].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        {!locked && noSheet && (
          <Button size="small" startIcon={<UploadIcon />} disabled={busy} onClick={onAddWorksheet}>Add the worksheet</Button>
        )}
        {!locked && (
          <Button size="small" startIcon={<RecheckIcon />} disabled={busy} onClick={onRecheck}>Check again</Button>
        )}
      </Stack>
      {items.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {shown.map((i) => <Item key={i.key} item={i} locked={locked} busy={busy} onUndo={onUndo} />)}
          {items.length > FOLDED && (
            <Button size="small" onClick={() => setOpen((o) => !o)} sx={{ mt: 0.25, px: 0.5 }}>
              {open ? 'Show less' : `Show all ${items.length}`}
            </Button>
          )}
        </Box>
      )}
    </Card>
  );
};

export default WeekReviewCard;
